import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase';

export const expenseService = {
  // Create new expense
  async createExpense(expenseData) {
    try {
      const docRef = await addDoc(collection(db, 'expenses'), {
        ...expenseData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get expense by ID
  async getExpense(expenseId) {
    try {
      const expenseDoc = await getDoc(doc(db, 'expenses', expenseId));
      if (expenseDoc.exists()) {
        return { id: expenseDoc.id, ...expenseDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update expense
  async updateExpense(expenseId, updates) {
    try {
      const expenseRef = doc(db, 'expenses', expenseId);
      await updateDoc(expenseRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      throw error;
    }
  },

  // Get all expenses (Admin function)
  async getAllExpenses() {
    try {
      const expensesCollection = collection(db, 'expenses');
      const expensesSnapshot = await getDocs(expensesCollection);
      const expenses = [];
      
      expensesSnapshot.forEach((doc) => {
        expenses.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return expenses;
    } catch (error) {
      console.error('Error getting all expenses:', error);
      throw error;
    }
  },

  // Get expenses by ticket ID (optional - for expenses tied to tickets)
  async getExpensesByTicket(ticketId) {
    try {
      const q = query(
        collection(db, 'expenses'),
        where('ticketId', '==', ticketId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const expenses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort expenses client-side for now
      expenses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return expenses;
    } catch (error) {
      throw error;
    }
  },

  // Get expenses by owner ID (all expenses for an owner)
  async getExpensesByOwner(ownerId) {
    try {
      const q = query(
        collection(db, 'expenses'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const expenses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort expenses client-side for now
      expenses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return expenses;
    } catch (error) {
      throw error;
    }
  },

  // Get expenses by property ID
  async getExpensesByProperty(propertyId) {
    try {
      const q = query(
        collection(db, 'expenses'),
        where('propertyId', '==', propertyId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const expenses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort expenses client-side for now
      expenses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return expenses;
    } catch (error) {
      throw error;
    }
  },

  // Get total expenses for a ticket
  async getTicketTotalExpenses(ticketId) {
    try {
      const expenses = await this.getExpensesByTicket(ticketId);
      return expenses.reduce((total, expense) => total + (expense.amount || 0), 0);
    } catch (error) {
      throw error;
    }
  },

  // Get total expenses for a property
  async getPropertyTotalExpenses(propertyId) {
    try {
      const expenses = await this.getExpensesByProperty(propertyId);
      return expenses.reduce((total, expense) => total + (expense.amount || 0), 0);
    } catch (error) {
      throw error;
    }
  },

  // Listen to expenses by ticket in real-time
  subscribeToExpensesByTicket(ticketId, callback) {
    const q = query(
      collection(db, 'expenses'),
      where('ticketId', '==', ticketId)
      // Temporarily removed orderBy to avoid index requirement
    );

    return onSnapshot(q, (querySnapshot) => {
      const expenses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort expenses client-side for now
      expenses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      callback(expenses);
    });
  },

  // Listen to expenses by property in real-time
  subscribeToExpensesByProperty(propertyId, callback) {
    const q = query(
      collection(db, 'expenses'),
      where('propertyId', '==', propertyId)
      // Temporarily removed orderBy to avoid index requirement
    );

    return onSnapshot(q, (querySnapshot) => {
      const expenses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort expenses client-side for now
      expenses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      callback(expenses);
    });
  },

  // Listen to expenses by owner in real-time
  subscribeToExpensesByOwner(ownerId, callback) {
    const q = query(
      collection(db, 'expenses'),
      where('ownerId', '==', ownerId)
      // Temporarily removed orderBy to avoid index requirement
    );

    return onSnapshot(q, (querySnapshot) => {
      const expenses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort expenses client-side for now
      expenses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      callback(expenses);
    });
  }
}; 